import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageContainerService } from '../../services';
import { ControllerForViewBase } from '../framework/controller-for-view-base';
import { GetProjectsByUserSessionCommandRequest, GetProjectsByUserSessionCommandResponse, ProjectAccessDto } from '../../data/CommandServiceDtos.generated';
import { faTimes, faChevronRight, faChevronDown, faFolderOpen } from '@fortawesome/free-solid-svg-icons';

@Component({
  standalone: false,
  selector: 'app-accessed-projects',
  templateUrl: './accessed-projects.component.html',
  styleUrls: ['./accessed-projects.component.scss'],
})
export class AccessedProjectsComponent extends ControllerForViewBase implements OnInit {
  faTimes = faTimes;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faFolderOpen = faFolderOpen;

  /** Project ids whose per-page breakdown is open. */
  expandedProjectIds = new Set<number>();

  // data will be passed as { session: any }
  projects: Array<any> = [];
  totalActiveHours: string = '0m';
  isLoading = false;

  constructor(public dialogRef: MatDialogRef<AccessedProjectsComponent>, @Inject(MAT_DIALOG_DATA) public data: any, public pcs: PageContainerService) {
    super(pcs);
  }

  ngOnInit() {
    this.loadData();
  }

  async loadData() {
    this.isLoading = true;
    const request = new GetProjectsByUserSessionCommandRequest();
    request.ProjectAccessDto = new ProjectAccessDto();
    request.ProjectAccessDto.Username = this.data.session.UserLoginName;
    request.ProjectAccessDto.TimeIn = this.data.session.SessionStart;
    request.ProjectAccessDto.TimeZone = String(new Date().getTimezoneOffset() * -1);
    try {
      const response: GetProjectsByUserSessionCommandResponse = await this.pcs.apiService.GetProjectsByUserSession(request);
      this.projects = (response.Result.Data || []).map((p: any) => {
        if (p.LastAccessedOn && typeof p.LastAccessedOn === 'string' && !p.LastAccessedOn.endsWith('Z') && !p.LastAccessedOn.includes('+')) {
          p.LastAccessedOn += 'Z';
        }
        return p;
      });
      this.projects.forEach(p => this.expandedProjectIds.add(p.ProjectId));
      this.calculateTotalActiveHours();
    } finally {
      this.isLoading = false;
    }
  }

  toggleBreakdown(project: any) {
    if (this.expandedProjectIds.has(project.ProjectId)) {
      this.expandedProjectIds.delete(project.ProjectId);
    } else {
      this.expandedProjectIds.add(project.ProjectId);
    }
  }

  isExpanded(project: any): boolean {
    return this.expandedProjectIds.has(project.ProjectId);
  }

  hasBreakdown(project: any): boolean {
    return !!(project.AreaBreakdown && project.AreaBreakdown.length > 0);
  }

  /** hh:mm from the server, shown the same way as the project total. */
  private formatMinutes(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return (hours > 0 ? hours + 'h ' : '') + minutes + 'm';
  }

  calculateTotalActiveHours() {
    let totalMinutes = 0;

    if (this.projects && this.projects.length > 0) {
      this.projects.forEach(project => {
        if (project.TotalTimeSpent) {
          const parts = project.TotalTimeSpent.split(':');
          if (parts.length >= 2) {
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            const durationInMinutes = hours * 60 + minutes;
            totalMinutes += durationInMinutes;

            // Update project display format
            const displayHours = Math.floor(durationInMinutes / 60);
            const displayMinutes = durationInMinutes % 60;
            project.TotalTimeSpent = (displayHours > 0 ? displayHours + 'h ' : '') + displayMinutes + 'm';
          }
        }

        (project.AreaBreakdown || []).forEach(area => {
          area.TotalTimeSpent = this.formatMinutes(area.TotalMinutes || 0);
        });
      });
    }

    const overallHours = Math.floor(totalMinutes / 60);
    const overallMinutes = totalMinutes % 60;
    this.totalActiveHours = (overallHours > 0 ? overallHours + 'h ' : '') + overallMinutes + 'm';
  }

  close() { this.dialogRef.close(); }
}
